import React from "react";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { fmt, img } from "@/lib/api";

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, removeItem, totals, settings } = useCart();
  const nav = useNavigate();

  if (!open) return null;

  const goCheckout = () => {
    setOpen(false);
    nav("/checkout");
  };

  const progressPct = Math.min(100, (Math.min(totals.subtotal - totals.bundleDiscount, settings.free_shipping_threshold) / settings.free_shipping_threshold) * 100);

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} data-testid="cart-backdrop" />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-obsidian border-l border-bone-10 flex flex-col reveal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-bone-10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-bone/40">Your Bag</div>
            <div className="font-display text-2xl text-bone">CART / {String(totals.totalQty).padStart(2, "0")}</div>
          </div>
          <button onClick={() => setOpen(false)} className="text-bone/70 hover:text-imperial" data-testid="cart-close">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-bone-10">
          {totals.freeShip ? (
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-imperial">✓ Free U.S. Shipping unlocked</div>
          ) : (
            <>
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-bone/70 mb-2">
                Add {fmt(totals.toFreeShip)} for FREE shipping
              </div>
              <div className="units-meter"><div style={{ width: `${progressPct}%` }} /></div>
            </>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6" data-testid="cart-items">
          {items.length === 0 && (
            <div className="text-center py-16">
              <div className="font-display text-3xl text-bone mb-3">EMPTY BAG</div>
              <p className="text-bone/50 text-sm">Every grail starts as a wish.</p>
            </div>
          )}
          {items.map((i) => (
            <div key={i.key} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
              <div className="w-20 h-24 bg-ink shrink-0 border border-bone-10">
                <img src={img(i.image_url, 160)} alt={i.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-headline text-lg text-bone leading-tight">{i.name}</div>
                <div className="font-mono text-[10px] tracking-widest uppercase text-bone/50 mt-1">
                  {i.color} / {i.size}
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-bone-20">
                    <button onClick={() => updateQty(i.key, i.quantity - 1)} className="px-2 py-1 text-bone/70 hover:text-imperial" data-testid={`cart-decr-${i.product_id}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="px-3 font-mono text-xs text-bone">{i.quantity}</span>
                    <button onClick={() => updateQty(i.key, i.quantity + 1)} className="px-2 py-1 text-bone/70 hover:text-imperial" data-testid={`cart-incr-${i.product_id}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="font-mono text-sm text-bone">{fmt(i.price * i.quantity)}</div>
                </div>
              </div>
              <button onClick={() => removeItem(i.key)} className="text-bone/40 hover:text-crown" aria-label="Remove" data-testid={`cart-remove-${i.product_id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-bone-10 px-6 py-6 space-y-3">
          <div className="flex justify-between font-mono text-xs text-bone/70">
            <span>SUBTOTAL</span>
            <span>{fmt(totals.subtotal)}</span>
          </div>
          {totals.bundleDiscount > 0 && (
            <div className="flex justify-between font-mono text-xs text-imperial">
              <span>BUNDLE ({settings.bundle_discount_pct}% off {settings.bundle_threshold_items}+ items)</span>
              <span>−{fmt(totals.bundleDiscount)}</span>
            </div>
          )}
          <div className="flex justify-between font-mono text-xs text-bone/70">
            <span>SHIPPING</span>
            <span>{totals.shipping === 0 ? "FREE" : fmt(totals.shipping)}</span>
          </div>
          <div className="flex justify-between font-display text-2xl text-bone">
            <span>TOTAL</span>
            <span>{fmt(totals.total)}</span>
          </div>
          <button
            onClick={goCheckout}
            disabled={items.length === 0}
            className="btn-primary w-full mt-3 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cart-checkout-button"
          >
            SECURE CHECKOUT →
          </button>
          <p className="font-mono text-[10px] tracking-widest uppercase text-bone/40 text-center">Stripe payments launching soon</p>
        </div>
      </aside>
    </div>
  );
}
